import React, { useCallback, useEffect, useMemo } from 'react';
import { useField, useForm } from 'react-final-form';
import { observer } from 'mobx-react';
import { CheckboxField } from 'ksnm-common-ui/lib/Form/CheckboxField';
import { TextField } from 'ksnm-common-ui/lib/Form/InputFields';
import { SelectField } from 'ksnm-common-ui/lib/Form/SelectField';
import { TextAreaField } from 'ksnm-common-ui/lib/Form/TextAreaField';

import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';
import { organizationsStore } from '@stores/dictionaryStores/organizationsStore';
import { reportSettingEditStore } from '@stores/dictionaryStores/reportSettings/reportSettingEditStore';
import { LEVELS, reportSettingsStore } from '@stores/dictionaryStores/reportSettings/reportSettingsStore';
import { reportVersionsStore } from '@stores/dictionaryStores/reportVersion/reportVersionsStore';
import type { ReportVersionDto } from '@src/api';
import type { ReportSettingLevelDtoDefId } from '@stores/dictionaryStores/reportSettings/reportSettingsStore';

import { UrlParamFields } from './UrlParamFields';

const renderReportVersion = (value: ReportVersionDto) => `${value.reportCodeExt} - ${value.version} - ${value.name}`;
const deltaApproachLevels = [LEVELS.LEVEL3, LEVELS.LEVEL5, LEVELS.LEVEL6, LEVELS.LEVEL7, LEVELS.LEVEL9, LEVELS.LEVEL10];

export const MainFields = observer(() => {
  const { isNew, isEditMode } = reportSettingEditStore;

  const { reportVersions } = reportVersionsStore;
  const { organizations } = organizationsStore;
  const { selectedKeys, reportSettingsLevel } = reportSettingsStore;
  const { change } = useForm();

  useEffect(() => {
    fetchReferences(['reportVersions', 'organizations']);
  }, []);

  const {
    input: { value: levelId, onChange: changeLevelId },
  } = useField('levelId');

  const reportVersionsItems = useMemo(() => {
    const group = selectedKeys[0]?.split(' - ')[1]; // ключ вида organizationCode - reportGroupCode - и т.д.
    if (group) return reportVersions.filter((item) => item.reportGroupCode === group);

    return reportVersions;
  }, [reportVersions, selectedKeys]);

  const onChange = useCallback(
    (value) => {
      if (levelId === LEVELS.LEVEL4 || levelId === LEVELS.LEVEL4_D01) {
        const group = reportVersions?.find((item) => item.code === value)?.reportGroupCode;
        const nextUrl = group === 'D01' ? LEVELS.LEVEL4_D01 : LEVELS.LEVEL4;

        changeLevelId(nextUrl);

        const selectLevel = reportSettingsLevel.find((item) => item.id === nextUrl);
        if (selectLevel) {
          ['urlP1', 'urlP2', 'urlP3', 'urlP4', 'urlP5', 'urlP6', 'urlP7', 'urlP8', 'urlP9', 'urlP10'].forEach((item) =>
            change(item, selectLevel[item as keyof ReportSettingLevelDtoDefId]),
          );
        }
      }
    },
    [change, changeLevelId, reportSettingsLevel, reportVersions, levelId],
  );

  return (
    <>
      <SelectField
        name="organizationCode"
        items={organizations}
        label="Код компании"
        size="small"
        groupStyle={!isEditMode}
        allowClear
        showSearch
        disabled
      />
      <SelectField
        name="reportVersionCode"
        items={reportVersionsItems}
        label="Код версии отчетной формы"
        size="small"
        groupStyle={!isEditMode}
        disabled={!isNew}
        renderName={renderReportVersion}
        required
        onChange={onChange}
      />
      <TextField
        name="tableSource"
        label="Таблица-источник для построения отчета"
        size="small"
        groupStyle
        maxLength={63}
        disabled={!isEditMode}
      />
      <TextField
        name="tableSourceClient"
        label="Таблица-источник клиента"
        size="small"
        groupStyle
        maxLength={255}
        disabled={!isEditMode}
      />
      <TextAreaField
        rows={1}
        name="fileStructure"
        label="Структура-источник файла"
        size="small"
        groupStyle
        maxLength={200}
        autoSize
        disabled={!isEditMode}
      />
      <TextField
        name="tableReceiver"
        label="Таблица-получатель"
        size="small"
        groupStyle
        maxLength={63}
        disabled={!isEditMode}
      />
      <TextField
        name="uiTableSource"
        label="Источник для отображения отчета на UI"
        size="small"
        groupStyle
        maxLength={63}
        disabled={!isEditMode}
      />
      <UrlParamFields />

      <TextField
        name="script"
        label="Процедура наполнения отчета"
        size="small"
        groupStyle
        maxLength={255}
        disabled={!isEditMode}
      />

      <CheckboxField
        name="rounded"
        label="Округление значений"
        groupStyle
        disabled={!isEditMode || (levelId !== LEVELS.LEVEL1 && levelId !== LEVELS.LEVEL2)}
      />
      <CheckboxField
        name="isReportStructureVisible"
        label="Отображать в структуре отчетности"
        groupStyle
        disabled={!isEditMode}
      />
      <CheckboxField
        name="isReportPublishVisible"
        label="Отображать в реестре публикаций"
        groupStyle
        disabled={!isEditMode}
      />
      {deltaApproachLevels.includes(levelId) && (
        <CheckboxField name="isDeltaApproach" label="Дельта-подход" groupStyle disabled={!isEditMode} />
      )}
    </>
  );
});
